import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Heart, Building2, PartyPopper, ArrowRight } from "lucide-react";
import { IMAGES } from "../config";

const Services = () => {
  const services = [
    {
      title: "Wedding Bar Hire",
      description: "Make your special day unforgettable with our bespoke wedding bar packages. From intimate ceremonies to grand celebrations.",
      image: IMAGES.weddingToast,
      link: "/weddings",
      icon: <Heart size={24} />,
      alt: "Wedding bar hire — champagne toast at a mobile bar event"
    },
    {
      title: "Private & Corporate",
      description: "Professional bar services for birthdays, corporate events, and private celebrations.",
      image: IMAGES.friends,
      link: "/private-hire",
      icon: <Building2 size={24} />,
      alt: "Corporate event bar hire — mobile bar at a private party"
    },
    {
      title: "Festival & Events",
      description: "High-capacity solutions for festivals and large-scale events.",
      image: IMAGES.outdoorParty,
      link: "/festivals",
      icon: <PartyPopper size={24} />,
      alt: "Festival bar hire — outdoor mobile bar setup for large events"
    }
  ];

  return (
    <section className="py-20 md:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <p className="text-[#a5dae0] font-script text-2xl mb-4">Our Services</p>
          <h2 className="text-4xl md:text-5xl">What We Offer</h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="md:row-span-2 group"
          >
            <Link
              to={services[0].link}
              data-testid="service-wedding"
              className="block relative h-full min-h-[500px] rounded-2xl overflow-hidden"
            >
              <img
                src={services[0].image}
                alt={services[0].alt}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#1a1a1a]/80 via-transparent to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-8">
                <div className="flex items-center gap-2 text-[#a5dae0] mb-3">
                  {services[0].icon}
                  <span className="text-sm font-medium uppercase tracking-wider">Most Popular</span>
                </div>
                <h3 className="text-3xl text-white mb-3">{services[0].title}</h3>
                <p className="text-white/80 mb-4">{services[0].description}</p>
                <span className="inline-flex items-center gap-2 text-[#a5dae0] font-medium group-hover:gap-4 transition-all">
                  Learn More <ArrowRight size={18} />
                </span>
              </div>
            </Link>
          </motion.div>

          {services.slice(1).map((service, i) => (
            <motion.div
              key={service.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className="group"
            >
              <Link
                to={service.link}
                data-testid={`service-${service.title.toLowerCase().replace(/[^a-z]/g, "-")}`}
                className="block relative h-[240px] rounded-2xl overflow-hidden"
              >
                <img
                  src={service.image}
                  alt={service.alt}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#1a1a1a]/80 via-transparent to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <div className="flex items-center gap-2 text-[#a5dae0] mb-2">
                    {service.icon}
                  </div>
                  <h3 className="text-2xl text-white mb-2">{service.title}</h3>
                  <p className="text-white/80 text-sm">{service.description}</p>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Services;
